import mixin from 'privates/utils/mixin';
import Element from 'ui/Element';
import LinkableMixin from './mixins/LinkableMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from './Utils.es6';
import * as members from 'privates/privateMembers';
import { validate } from 'privates/validations/validation';
import _ from 'lodash';

/**
 * @class RatingsDisplay
 * @summary A Rating Display element allows you to display a value to your users
 *  using icons.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.LinkableMixin
 * @memberof $w
 * @tagname ratingsDisplay
 * @viewername wixui.ratingsDisplay
 */
class RatingsDisplay extends Element {
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        super(RMI, compId, contextId, wixSelector, controllerId, role, type);
        mixin(RatingsDisplay.prototype, HiddenCollapsedMixin.prototype);
        mixin(RatingsDisplay.prototype, ClickableMixin.prototype);
        mixin(RatingsDisplay.prototype, LinkableMixin.prototype);
    }

    /**
     * @member rating
     * @label property
     * @syntax
     * get rating(): Number
     * set rating(value: Number): void
     * @summary Sets or gets the displayed rating.
     *  Setting the `rating` property sets how many icons are displayed in the on
     *  state and what value to show textually if the element is set to show a textual
     *  value.
     *
     *  Set the `rating` value to a number between 1.0 and 5.0 to display a rating.
     *  The textual value, if set to show, will display the `rating` value to the
     *  nearest tenth and the rating icons will round that value to the nearest half
     *  icon. For example, if you set `rating` to 4.24, it will be show textually as
     *  4.2 and 4 stars will be displayed in the on state. However, if you set it to
     *  4.25, it will be show textually as 4.3 and 4 and a half stars will be displayed
     *  in the on state.
     *
     *  To reset the rating display element, restoring it to the empty state, set the value
     *  property to `null` or `undefined`.
     *
     *  Getting the `rating` property returns the current displayed rating.
     * @snippet [RatingDisplay-rating_get.es6=Get the displayed rating]
     * @snippet [RatingDisplay-rating_set.es6=Set the displayed rating]
     * @type {external:Number}
     * @memberof $w.RatingsDisplay
     * @readonly
     */
    get rating () {
        return Utils.getData(this, 'rating');
    }

    /**
     * @param {external:Number} rating
     * @ignore
     */
    set rating (rating) {
        const compType = members._type.get(this).compType;
        const valid = validate({compType, functionName: 'rating', dataToValidate: {rating}});

        if (!valid || _.isNil(rating)) {
            rating = null;
            Utils.setData(this, { rating, numRatings: 0 });
            return;
        }

        // Round rating to one decimal place
        rating = _.round(rating, 1);

        Utils.setData(this, { rating });
    }

    /**
     * @member numRatings
     * @label property
     * @syntax
     * get numRatings(): Number
     * set numRatings(value: Number): void
     * @summary Sets or gets the displayed number of ratings.
     *  Setting the `numRatings` property sets the displayed number of ratings if the
     *  element is set to show the number of ratings.
     *
     *  Set the `numRatings` value to a non-negative number.
     *
     *  Getting the `numRatings` property returns the current number of ratings.
     * @snippet [RatingDisplay-numRatings_get.es6=Get the displayed rating]
     * @snippet [RatingDisplay-numRatings_set.es6=Set the displayed rating]
     * @type {external:Number}
     * @memberof $w.RatingsDisplay
     * @readonly
     */
    get numRatings () {
        return Utils.getData(this, 'numRatings');
    }

    /**
     * @param {external:Number} number of ratings
     * @ignore
     */
    set numRatings (numRatings) {
        const compType = members._type.get(this).compType;

        if (!validate({compType, functionName: 'numRatings', dataToValidate: {numRatings}})) {
            return;
        }

        if(_.isNil(numRatings)) {
            numRatings = 0;
        }

        Utils.setData(this, { numRatings });
    }

    toJSON() {
        const {id, type, global, rendered, rating, numRatings} = this;
        return {id, type, global, rendered, rating, numRatings};
    }
}

export default RatingsDisplay;



// WEBPACK FOOTER //
// ./js/modules/ui/RatingsDisplay.es6